import type { Comment } from '@/@types/comment'
import type { Story, StoryFeed } from '@/@types/story'
import { StoryFeedType, StoryStatus } from '@/@types/story'
import {
	GET_STORY,
	LIKE_STORY,
	LIST_USER_STORIES,
	MEDIA_META,
	READ_STORY,
	STORY_FOLLOWING_FEED,
	UNLIKE_STORY,
} from '@/constants/api'
import { get, post } from '@/utils/request'
import {
	type InfiniteData,
	type QueryClient,
	type UseInfiniteQueryOptions,
	type UseQueryOptions,
} from '@tanstack/react-query'
import { fromByteArray, toByteArray } from 'base64-js'
import type { ListPage } from './post'

interface MediaInteractiveResponse {
	url?: string | null
	body?: string | null
	next?: string | null
	headers?: Record<string, string> | null
}

interface MediaContinuationRequest {
	func: string
	body?: string
}

type StoryUpdater = (story: Story) => Story

const MAX_MEDIA_HANDSHAKE_RETRIES = 4
const USER_STORIES_STALE_TIME = 5 * 60 * 1000

const FOLLOWING_STORIES_QUERY_KEY = [STORY_FOLLOWING_FEED] as const

const getUserStoriesQueryKey = (username: string) => [LIST_USER_STORIES, username] as const

const getStoryQueryKey = (id: string) => [GET_STORY, id] as const

const getStoryMediaQueryKey = (id: string) => [MEDIA_META, 'STORY', id] as const

const requestExternalMedia = async (response: MediaInteractiveResponse): Promise<Uint8Array> => {
	if (!response.url) throw new Error('媒体握手缺少外部请求地址')

	const request: RequestInit = {
		method: response.body ? 'POST' : 'GET',
		headers: response.headers ?? undefined,
		body: response.body ? toByteArray(response.body) : undefined,
	}
	const externalResponse = await fetch(response.url, request)

	if (!externalResponse.ok) {
		throw new Error(`媒体握手请求失败：${externalResponse.status}`)
	}

	return new Uint8Array(await externalResponse.arrayBuffer())
}

const resolveStoryMedia = async (id: string): Promise<string> => {
	const params = { id, type: 'STORY', trigger: 'user' }
	let response = await post<MediaInteractiveResponse>(MEDIA_META, {}, { params }).then(
		(result) => result.data
	)
	let retries = 0

	while (response.next) {
		const bytes = await requestExternalMedia(response).catch(() => new Uint8Array())
		const request: MediaContinuationRequest = bytes.length
			? { func: response.next, body: fromByteArray(bytes) }
			: { func: 'fallback' }

		response = await post<MediaInteractiveResponse>(MEDIA_META, request, { params }).then(
			(result) => result.data
		)

		if (response.next && retries >= MAX_MEDIA_HANDSHAKE_RETRIES) {
			throw new Error('媒体握手重试次数已用尽')
		}

		retries += 1
	}

	if (!response.url) throw new Error('媒体服务未返回可播放地址')

	return response.url
}

export const FOLLOWING_STORIES_QUERY_OPTIONS: UseQueryOptions<StoryFeed[]> = {
	queryKey: FOLLOWING_STORIES_QUERY_KEY,
	queryFn: () =>
		get<ListPage<StoryFeed>>(STORY_FOLLOWING_FEED, { suppressToast: true }).then(
			(response) => response.data.data
		),
	retry: 2,
}

export const getUserStoriesQueryOptions = (
	username: string
): UseInfiniteQueryOptions<ListPage<Story>, Error, InfiniteData<ListPage<Story>>> => ({
	queryKey: getUserStoriesQueryKey(username),
	queryFn: ({ pageParam }) =>
		get<ListPage<Story>>(LIST_USER_STORIES, {
			params: { username, loadMoreKey: pageParam },
		}).then((response) => response.data),
	getNextPageParam: (lastPage) => lastPage.loadMoreKey,
	initialPageParam: undefined,
	staleTime: USER_STORIES_STALE_TIME,
})

export const getStoryQueryOptions = (id: string): UseQueryOptions<Story> => ({
	queryKey: getStoryQueryKey(id),
	queryFn: () =>
		get<{ data: Story }>(GET_STORY, { params: { id } }).then((response) => response.data.data),
	staleTime: USER_STORIES_STALE_TIME,
})

export const getStoryMediaQueryOptions = (id: string): UseQueryOptions<string> => ({
	queryKey: getStoryMediaQueryKey(id),
	queryFn: () => resolveStoryMedia(id),
	enabled: Boolean(id),
	retry: false,
})

export const readStory = (id: string, type: 'STORY' | 'STORY_RESOURCE' = 'STORY') =>
	post(READ_STORY, { id, type }, { suppressToast: true }).then(() => undefined)

export const likeStory = (id: string) => post(LIKE_STORY, { id }).then(() => undefined)

export const unlikeStory = (id: string) => post(UNLIKE_STORY, { id }).then(() => undefined)

const updateStoryInCache = (queryClient: QueryClient, storyId: string, updater: StoryUpdater) => {
	queryClient.setQueriesData<InfiniteData<ListPage<Story>>>(
		{ queryKey: [LIST_USER_STORIES] },
		(data) => {
			if (!data) return data

			let changed = false
			const pages = data.pages.map((page) => {
				let pageChanged = false
				const stories = page.data.map((story) => {
					if (story.id !== storyId) return story

					changed = true
					pageChanged = true
					return updater(story)
				})

				return pageChanged ? { ...page, data: stories } : page
			})

			return changed ? { ...data, pages } : data
		}
	)
	queryClient.setQueriesData<Story>({ queryKey: [GET_STORY] }, (story) =>
		story?.id === storyId ? updater(story) : story
	)
}

export const setStoryLikedInCache = (queryClient: QueryClient, storyId: string, liked: boolean) => {
	updateStoryInCache(queryClient, storyId, (story) =>
		story.liked === liked
			? story
			: {
					...story,
					liked,
					likeCount: Math.max(0, story.likeCount + (liked ? 1 : -1)),
				}
	)
}

export const addStoryCommentToCache = (
	queryClient: QueryClient,
	storyId: string,
	comment: Comment
) => {
	updateStoryInCache(queryClient, storyId, (story) => ({
		...story,
		commentCount: story.commentCount + 1,
		attachedComments: [comment, ...story.attachedComments],
	}))
}

export const addStoryCommentReplyToCache = (queryClient: QueryClient, storyId: string) => {
	updateStoryInCache(queryClient, storyId, (story) => ({
		...story,
		commentCount: story.commentCount + 1,
	}))
}

export const removeStoryCommentFromCache = (
	queryClient: QueryClient,
	storyId: string,
	comment: Comment
) => {
	if (!comment?.id) return
	const isPrimaryComment = comment.level <= 1
	const removedCommentCount = isPrimaryComment ? 1 + comment.replyCount : 1

	updateStoryInCache(queryClient, storyId, (story) => ({
		...story,
		commentCount: Math.max(0, story.commentCount - removedCommentCount),
		attachedComments: isPrimaryComment
			? story.attachedComments.filter((item) => item.id !== comment.id)
			: story.attachedComments,
	}))
}

export const markStoryFeedUserReadInCache = (queryClient: QueryClient, username: string) => {
	queryClient.setQueryData<StoryFeed[]>(FOLLOWING_STORIES_QUERY_KEY, (feeds) =>
		feeds?.map((feed) =>
			feed.type === StoryFeedType.USER_STORY &&
			feed.user.username === username &&
			feed.user.storyStatus !== StoryStatus.READ
				? { ...feed, user: { ...feed.user, storyStatus: StoryStatus.READ } }
				: feed
		)
	)
}

export const removeStoryResourceFromCache = (queryClient: QueryClient, resourceId: string) => {
	queryClient.setQueryData<StoryFeed[]>(FOLLOWING_STORIES_QUERY_KEY, (feeds) =>
		feeds?.filter(
			(feed) => feed.type !== StoryFeedType.STORY_RESOURCE || feed.data.id !== resourceId
		)
	)
}
